import { useStore, selectSettings } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { COLORS, FONT, SPACING } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from './modal-shared';
import { CloudSection } from './CloudSection';

const ASSET_ROOT = '/assets/generated/house';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SettingsModal({ open, onClose }: Props) {
  const t = useT();
  const s = useStore(selectSettings);
  const setLocale = useStore((x) => x.setLocale);
  const setMaster = useStore((x) => x.setMasterVolume);
  const setSfx = useStore((x) => x.setSfxVolume);
  const setAmbient = useStore((x) => x.setAmbientVolume);
  const setMusic = useStore((x) => x.setMusicVolume);
  const setMuted = useStore((x) => x.setMuted);
  const setFontScale = useStore((x) => x.setFontScale);
  const setReducedMotion = useStore((x) => x.setReducedMotion);
  const setFastMode = useStore((x) => x.setFastMode);

  if (!open) return null;
  return (
    <div role="dialog" data-testid="settings-modal" style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={title}>
          <img src={`${ASSET_ROOT}/icon-settings.png`} alt="" draggable={false} style={titleIcon} />
          {t('settings.title')}
        </h2>

        <Section label={t('settings.locale')}>
          <button data-testid="settings-locale-pt" onClick={() => setLocale('pt-BR')} style={pillBtn(s.locale === 'pt-BR')}>
            PT-BR
          </button>
          <button data-testid="settings-locale-en" onClick={() => setLocale('en')} style={pillBtn(s.locale === 'en')}>
            EN
          </button>
        </Section>

        <Section label={t('settings.audio.master')}>
          <Slider testId="settings-vol-master" value={s.audio.masterVolume} onChange={setMaster} />
        </Section>
        <Section label={t('settings.audio.sfx')}>
          <Slider testId="settings-vol-sfx" value={s.audio.sfxVolume} onChange={setSfx} />
        </Section>
        <Section label={t('settings.audio.ambient')}>
          <Slider testId="settings-vol-ambient" value={s.audio.ambientVolume} onChange={setAmbient} />
        </Section>
        <Section label={t('settings.audio.music')}>
          <Slider testId="settings-vol-music" value={s.audio.musicVolume} onChange={setMusic} />
        </Section>
        <Section label={t('settings.audio.muted')}>
          <Toggle testId="settings-muted" checked={s.audio.muted} onChange={setMuted} />
        </Section>

        <Section label={t('settings.a11y.fontScale')}>
          <Slider
            testId="settings-font-scale"
            value={(s.accessibility.fontScale - 0.8) / 0.7}
            onChange={(v) => setFontScale(0.8 + v * 0.7)}
          />
        </Section>
        <Section label={t('settings.a11y.reducedMotion')}>
          <Toggle
            testId="settings-reduced-motion"
            checked={s.accessibility.reducedMotion}
            onChange={setReducedMotion}
          />
        </Section>

        <Section label={t('a11y.colorblind')}>
          <button
            data-testid="settings-cb-off"
            onClick={() => useStore.getState().setColorblindMode('off')}
            style={pillBtn(s.accessibility.colorblindMode === 'off')}
          >
            {t('a11y.colorblind.off')}
          </button>
          <button
            data-testid="settings-cb-deutero"
            onClick={() => useStore.getState().setColorblindMode('deutero')}
            style={pillBtn(s.accessibility.colorblindMode === 'deutero')}
          >
            {t('a11y.colorblind.deutero')}
          </button>
        </Section>

        <CloudSection />

        <Section label={t('settings.fastMode')}>
          <Toggle testId="settings-fast-mode" checked={s.fastMode} onChange={setFastMode} />
        </Section>

        <div style={{ textAlign: 'right', marginTop: SPACING.md }}>
          <button data-testid="settings-close" onClick={onClose} style={primaryBtn}>
            {t('settings.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: SPACING.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.md }}>
      <label style={{ fontSize: FONT.sizeSm, color: COLORS.textDim }}>{label}</label>
      <div style={{ display: 'flex', gap: SPACING.xs, alignItems: 'center' }}>{children}</div>
    </div>
  );
}

function Slider({ testId, value, onChange }: { testId: string; value: number; onChange: (v: number) => void }) {
  return (
    <input
      data-testid={testId}
      type="range"
      min={0}
      max={1}
      step={0.05}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: 140 }}
    />
  );
}

function Toggle({ testId, checked, onChange }: { testId: string; checked: boolean; onChange: (b: boolean) => void }) {
  return (
    <input
      data-testid={testId}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ width: 18, height: 18, cursor: 'pointer' }}
    />
  );
}

const title: React.CSSProperties = {
  margin: '0 0 16px 0',
  color: COLORS.accent,
  fontFamily: FONT.family,
  fontSize: FONT.sizeXl,
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.xs,
};

const titleIcon: React.CSSProperties = {
  width: 36,
  height: 36,
  objectFit: 'contain',
  imageRendering: 'auto',
};

const pillBtn = (active: boolean): React.CSSProperties => ({
  background: active ? COLORS.accent : 'transparent',
  color: active ? COLORS.bg : COLORS.textDim,
  border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
  borderRadius: 16,
  padding: '4px 12px',
  cursor: 'pointer',
  fontSize: FONT.sizeXs,
});

const primaryBtn: React.CSSProperties = {
  background: COLORS.accent,
  color: COLORS.bg,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontWeight: 600,
};
